import { useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { IconApplicants } from '@components/icons/applicants';
import { IconEmail } from '@components/icons/email';
import { IconOrdering } from '@components/icons/ordering';
import { IconPhone } from '@components/icons/phone';
import { Previewer } from '@components/previewer';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';

import { Box } from 'components/Box';
import NationalRegistry from 'components/icons/national-registry.png';
import { LargeApplicationCard } from 'components/LargeApplicationCard';
import { NamePlate } from 'components/NamePlate';
import { NationalRegistryInformation } from 'components/NationalRegistryInformation';
import { AnswerCard, QuestionAnswer } from 'components/QuestionAnswer';
import { useStore } from 'models/Provider';

export const ApplicationDetails = observer(({ application }) => {
  return (
    <Stack space={6} id="content-container" style={{ marginTop: '1.5rem' }}>
      <Stack space={0}>
        <ContactDetails application={application} />
      </Stack>
      <Separator />
      <ApplicationNationalRegistryInfo application={application} />
      <ApplicationsWithSameEmail application={application} />
      <ApplicationCV application={application} />
      <Separator />
      <Stack space={0}>
        <ApplicationCoverLetter application={application} />
      </Stack>
      <Separator />
      <Stack space={0}>
        <ApplicationAnswers application={application} />
      </Stack>
      {application.referredBy && (
        <>
          <Separator />
          <ApplicationSourcedBy application={application} />
        </>
      )}
    </Stack>
  );
});

const ApplicationNationalRegistryInfo = observer(({ application }) => {
  const { t } = useStore();

  if (!application.ssnRegistry) {
    return null;
  }

  return (
    <>
      <Box flex justifyContent={'space-between'} alignItems={'center'} flexDirection={'row'}>
        <Box flex center>
          <Box borderRadius="50%" overflow="hidden" mr="8">
            <img src={NationalRegistry} alt={'national-registry'} />
          </Box>
          <Text font="bold14" text={t('application.details.ssnregistry.header')} />
        </Box>
      </Box>
      <NationalRegistryInformation info={application.ssnRegistry} />
      <Separator />
    </>
  );
});

const ApplicationsWithSameEmail = observer(({ application }) => {
  const { api, t } = useStore();
  const [otherApplications, setOtherApplications] = useState([]);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await api.applications.findApplicationsByEmail(application.email);
      if (!res.error) {
        res.data && setOtherApplications(res.data.results.filter((a) => a.id !== application.id));
      }
    })();
  }, [api.applications, application]);
  if (otherApplications.length <= 0) {
    return null;
  }

  return (
    <>
      <Stack space={6}>
        <Stack space={2}>
          <Box flex justifyContent="space-between" alignItems="center" flexDirection="row">
            <Text
              font="bold14"
              text={t('application.details.header.applied_to', otherApplications.length)}
            />
            {otherApplications.length > 1 && (
              <ShowAllOrCollapse setShowAll={setShowAll} showAll={showAll} />
            )}
          </Box>
          {showAll &&
            otherApplications.map((a) => <LargeApplicationCard key={a.id} application={a} />)}
          {!showAll && <LargeApplicationCard application={otherApplications[0]} />}
        </Stack>
        <Separator />
      </Stack>
    </>
  );
});

const ApplicationAnswers = observer(({ application }) => {
  const { t } = useStore();
  return (
    <Stack space={4}>
      <Text font="bold14" text={t('application.header.answers')} />
      {application.formQuestions.map((q) => (
        <Stack space={4} key={q.id}>
          <QuestionAnswer q={q} application={application} key={q.id || q.title} />
        </Stack>
      ))}
    </Stack>
  );
});

const ApplicationSourcedBy = ({ application }) => {
  const { t } = useStore();
  const { name, picture } = application.referredBy;

  return (
    <Stack space={2}>
      <Text font="bold14" text={t('application.header.sourced')} />
      <AnswerCard>
        <Box flex alignItems="center">
          <NamePlate pic={picture} name={name} size="md" />
        </Box>
      </AnswerCard>
    </Stack>
  );
};

const ContactDetails = observer(({ application }) => {
  const { t } = useStore();

  return (
    <Stack space={2}>
      {application.email && (
        <ContactEntry flex alignItems="center">
          <Stack space={4} orientation="horizontal" align="center">
            <IconEmail color="purple50" />
            <Text
              font="bold14"
              style={{ minWidth: '10rem' }}
              text={t('application.contact.email')}
            />
            <ActionText.ExternalLink color="purple100" href={'mailto:' + application.email}>
              {application.email}
            </ActionText.ExternalLink>
          </Stack>
        </ContactEntry>
      )}
      {application.phone && (
        <ContactEntry flex alignItems="center">
          <Stack space={4} orientation="horizontal" align="center">
            <IconPhone color="purple50" />
            <Text
              font="bold14"
              style={{ minWidth: '10rem' }}
              text={t('application.contact.phone')}
            />
            <Text text={application.phone} />
          </Stack>
        </ContactEntry>
      )}
      {application.ssn && (
        <ContactEntry flex alignItems="center">
          <Stack space={4} orientation="horizontal" align="center">
            <IconApplicants color="purple50" />
            <Text font="bold14" style={{ minWidth: '10rem' }} text={t('application.contact.ssn')} />
            <Text text={application.ssn} />
          </Stack>
        </ContactEntry>
      )}
    </Stack>
  );
});

const ContactEntry = styled(Box)((p) => ({
  display: 'flex',
  alignItems: 'center',
  [p.theme.mobileQuery]: {
    '> span:not(:last-child)': {
      display: 'none',
    },
    '> span:last-child': {
      marginLeft: p.theme.spacing.m16,
    },
  },
}));

const ApplicationCV = observer(({ application }) => {
  const { t } = useStore();

  return (
    <Stack space={1}>
      <Text font="bold14" text={t('application.details.header.cv')} />
      {application.cv && (
        <ActionText.ExternalLink href={application.cvOriginal} ellipsis color="purple60">
          {application.cvFileName}
        </ActionText.ExternalLink>
      )}
      {!application.cv && (
        <Text font="regular16" color="purple60" text={t('application.details.nocvuploaded')} />
      )}
      {application.cv && (
        <Previewer
          key={application.id}
          url={application.cv}
          paginate
          maxWidth={1500}
          parentId={'content-container'}
          fallback={
            <Box mt="16">
              <Stack space={1}>
                <Text font="bold14">{t('application.details.nocvpreview')}</Text>
                <ActionText.ExternalLink href={application.cvOriginal} ellipsis color="purple60">
                  {t('application.clicktodownload')}
                </ActionText.ExternalLink>
              </Stack>
            </Box>
          }
        />
      )}
    </Stack>
  );
});

const ApplicationCoverLetter = observer(({ application }) => {
  const { t } = useStore();

  return (
    <Stack space={1}>
      <Text font="bold14" text={t('application.details.header.cl')} />
      {application.coverLetter && (
        <ActionText.ExternalLink href={application.coverLetterOriginal} ellipsis color="purple50">
          {application.coverLetterFileName}
        </ActionText.ExternalLink>
      )}
      {!application.coverLetter && (
        <Text font="regular16" color="purple60" text={t('application.details.nocluploaded')} />
      )}
      {application.coverLetter && (
        <Previewer
          url={application.coverLetter}
          paginate
          maxWidth={1500}
          parentId="content-container"
          fallback={
            <Box mt="24">
              <Text text={t('application.details.noclpreview')} />
              <ActionText.ExternalLink href={application.coverLetterOriginal}>
                {t('application.clicktodownload')}
              </ActionText.ExternalLink>
            </Box>
          }
        />
      )}
    </Stack>
  );
});

const ShowAllOrCollapse = ({ setShowAll, showAll }) => {
  const { t } = useStore();
  if (showAll) {
    return (
      <Box onClick={() => setShowAll(false)} color={'purple50'} flex alignItems={'center'}>
        <Text color="purple60" font="semibold14" text={t('application.details.header.collapse')} />
        <IconOrdering style={{ transform: 'rotate(180deg)' }} />
      </Box>
    );
  } else {
    return (
      <Box onClick={() => setShowAll(true)} color={'purple50'} flex alignItems={'center'}>
        <Text color="purple60" font="semibold14" text={t('application.details.header.show_all')} />
        <IconOrdering />
      </Box>
    );
  }
};
